/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 * 
 * Material UI Theme Configuration - Integrates theme system with MUI
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { typography, spacing } from './index';

/**
 * Read a resolved CSS custom-property value from :root / .dark.
 * Returns the trimmed hex/rgb string.  Falls back to `fallback` in
 * environments where the DOM is not available (e.g. Jest / JSDOM).
 */
const getCSSVar = (name: string, fallback = '#000000'): string => {
    if (typeof document === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    return value || fallback;
};

/**
 * Create a MUI theme by reading color tokens from designSystem.css.
 *
 * `designSystem.css` is the single source of truth for every color in the
 * application.  The `:root` selector holds light-mode values; the `.dark`
 * selector holds dark-mode overrides.  Because ThemeContext toggles the
 * `.dark` class on `<html>` *before* React re-renders, `getComputedStyle`
 * always returns the correct palette for the active mode.
 *
 * MUI v5 requires real color values (hex / rgb) in the palette — CSS
 * `var()` references are not supported there.  Component `styleOverrides`,
 * however, are plain CSS strings and can safely use `var(--*)`.
 */
export const createMuiTheme = () => {
    const muiThemeOptions: ThemeOptions = {
        palette: {
            primary: {
                main: getCSSVar('--primary'),
                light: getCSSVar('--secondary'),
                dark: getCSSVar('--sidebar-accent'),
                contrastText: getCSSVar('--primary-foreground'),
            },
            secondary: {
                main: getCSSVar('--secondary'),
                light: getCSSVar('--card-subtle'),
                dark: getCSSVar('--card-filled-foreground'),
                contrastText: getCSSVar('--secondary-foreground'),
            },
            success: {
                main: getCSSVar('--success'),
                light: getCSSVar('--success'),
                dark: getCSSVar('--success'),
                contrastText: getCSSVar('--destructive-foreground'),
            },
            error: {
                main: getCSSVar('--destructive'),
                light: getCSSVar('--destructive'),
                dark: getCSSVar('--destructive'),
                contrastText: getCSSVar('--destructive-foreground'),
            },
            warning: {
                main: getCSSVar('--warning'),
                light: getCSSVar('--warning'),
                dark: getCSSVar('--warning'),
                contrastText: getCSSVar('--destructive-foreground'),
            },
            info: {
                main: getCSSVar('--primary'),
                light: getCSSVar('--secondary'),
                dark: getCSSVar('--sidebar-accent'),
                contrastText: getCSSVar('--primary-foreground'),
            },
            background: {
                default: getCSSVar('--background'),
                paper: getCSSVar('--card'),
            },
            text: {
                primary: getCSSVar('--foreground'),
                secondary: getCSSVar('--subtle-foreground'),
                disabled: getCSSVar('--muted-foreground'),
            },
            divider: getCSSVar('--card-border'),
        },

    typography: {
        fontFamily: typography.fontFamily.base,
        fontSize: 16,
        fontWeightLight: typography.fontWeight.light,
        fontWeightRegular: typography.fontWeight.normal,
        fontWeightMedium: typography.fontWeight.medium,
        fontWeightBold: typography.fontWeight.bold,

        h1: {
            fontSize: typography.fontSize['4xl'],
            fontWeight: typography.fontWeight.bold,
            lineHeight: typography.lineHeight.tight,
            letterSpacing: typography.letterSpacing.tighter,
            textTransform: 'capitalize',
        },
        h2: {
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            lineHeight: typography.lineHeight.tight,
            letterSpacing: typography.letterSpacing.tight,
            textTransform: 'capitalize',
        },
        h3: {
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            lineHeight: typography.lineHeight.snug,
            textTransform: 'capitalize',
        },
        h4: {
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.semibold,
            lineHeight: typography.lineHeight.snug,
            textTransform: 'capitalize',
        },
        h5: {
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            lineHeight: typography.lineHeight.normal,
            textTransform: 'capitalize',
        },
        h6: {
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            lineHeight: typography.lineHeight.normal,
            textTransform: 'capitalize',
        },
        body1: {
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.normal,
            lineHeight: typography.lineHeight.normal,
        },
        body2: {
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.normal,
            lineHeight: typography.lineHeight.normal,
        },
        subtitle1: {
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            lineHeight: typography.lineHeight.relaxed,
        },
        subtitle2: {
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            lineHeight: typography.lineHeight.snug,
        },
        caption: {
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.normal,
            lineHeight: typography.lineHeight.snug,
        },
        overline: {
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.bold,
            lineHeight: typography.lineHeight.tight,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.wider,
        },
        button: {
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            lineHeight: typography.lineHeight.tight,
            textTransform: 'capitalize',
            letterSpacing: typography.letterSpacing.wide,
        },
    },

    spacing: (factor: number) => `${0.25 * factor}rem`,

    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1264,
            xl: 1920,
        },
    },

    shape: {
        borderRadius: 4,
    },

    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: typography.fontWeight.semibold,
                    borderRadius: 4,
                    padding: `${spacing[2]} ${spacing[4]}`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                },
                outlined: {
                    borderColor: 'var(--input)',
                    borderStyle: 'solid',
                    backgroundColor: 'transparent',
                    color: 'var(--foreground)',
                    '&:hover': {
                        borderColor: 'var(--input)',
                        backgroundColor: 'var(--accent)',
                    },
                },
                containedSecondary: {
                    borderColor: 'var(--input)',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    backgroundColor: 'transparent',
                    color: 'var(--foreground)',
                    boxShadow: 'none',
                    '&:hover': {
                        borderColor: 'var(--input)',
                        backgroundColor: 'var(--accent)',
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: 'none',
                    border: '1px solid var(--card-border)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 4,
                    },
                    '& label': {
                        textTransform: 'capitalize',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    color: 'var(--foreground)',
                },
                label: {
                    color: 'inherit',
                },
                deleteIcon: {
                    color: 'var(--muted-foreground)',
                    '&:hover': {
                        color: 'var(--foreground)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                '.sheet-title': {
                    fontWeight: '700 !important',
                },
            },
        },
    },
    };

    return createTheme(muiThemeOptions);
};

// Default export for backward compatibility
export const muiTheme = createMuiTheme();
export default muiTheme;
